import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PageSEO from "../../SEO/PageSEO";
import "./ServiceDetail.css";

// Import service images
import homeDoctorImg     from "../../../images/home-doctor-visit-uganda.jpeg";
import hospitalHomeImg   from "../../../images/hospital-at-home-care-uganda.jpeg";
import antenatalImg      from "../../../images/maternal-care-antenatal-uganda.jpeg";
import corporateImg      from "../../../images/corporate-health-assessment-uganda.jpeg";
import telemedImg        from "../../../images/telemedicine-consultation-uganda.jpeg";
import chronicImg        from "../../../images/chronic-disease-followup-uganda.jpeg";
import schoolImg         from "../../../images/school-medical-exams-uganda.jpeg";
import remoteImg         from "../../../images/remote-patient-monitoring-uganda.jpeg";
import communityImg      from "../../../images/community-health-outreach-kampala.jpeg";
import teamImg           from "../../../images/medical-team-yitalife-kampala.jpeg";

const services = {
  "doctor-home-visit": {
    title: "Doctor Home Visit in Uganda",
    heading: "Doctor Home Visits - Kampala & Uganda",
    description: "Book a qualified doctor to visit your home in Kampala or anywhere in Uganda. Yitalife's doctor home visit service brings expert medical care to your door - no queues, no transport stress. A service of Yita Medical Services (YMS).",
    image: homeDoctorImg,
    imageAlt: "Doctor conducting a home visit in Kampala, Uganda - Yitalife mobile healthcare",
    icon: "home-outline",
    intro: "Yitalife's Doctor Home Visit service brings fully qualified, licensed doctors directly to your home anywhere in Kampala and Central Uganda. Whether you are unwell, elderly, recovering from surgery, or simply prefer the convenience of care at home - our doctors come to you.",
    benefits: [
      "No hospital queues or long waiting times",
      "Doctor comes to your exact location - home, hotel, or office",
      "Same professional standard as any clinic or hospital",
      "Full diagnosis, prescriptions, and follow-up care",
      "Available 7 days a week, including evenings",
      "Particularly suited for elderly, children, and post-surgical patients",
    ],
    covered: ["General consultations", "Acute illness", "Wound dressing & injections", "Chronic condition review", "Prescription writing", "Post-surgical follow-up"],
    canonical: "https://www.yitalife.com/services/doctor-home-visit",
  },
  "hospital-at-home": {
    title: "Hospital at Home Care Uganda",
    heading: "Hospital at Home Care - Uganda",
    description: "Full hospital-level nursing and medical care delivered to your home in Uganda. IV drips, wound management, nursing care, and more - by Yitalife, a service of Yita Medical Services (YMS) in Kampala.",
    image: hospitalHomeImg,
    imageAlt: "Nurse providing hospital-at-home bedside care in Uganda - Yitalife",
    icon: "bed-outline",
    intro: "Hospital at Home from Yitalife provides the same level of clinical care you'd receive in a hospital ward - but from the comfort and dignity of your own home. Our team manages IV therapy, wound care, post-operative nursing, and complex medical monitoring without requiring hospital admission.",
    benefits: [
      "Reduces risk of hospital-acquired infections",
      "More comfortable recovery in familiar surroundings",
      "Significantly lower cost than inpatient hospital care",
      "Continuous nursing care and monitoring",
      "Daily medical reviews by our qualified doctors",
      "Family involvement in care and recovery",
    ],
    covered: ["IV fluid therapy", "Wound care & dressing", "Oxygen therapy", "Post-surgical nursing", "Daily doctor reviews", "Pain management"],
    canonical: "https://www.yitalife.com/services/hospital-at-home",
  },
  "antenatal-maternal-care": {
    title: "Antenatal & Maternal Care at Home - Uganda",
    heading: "Antenatal & Maternal Care",
    description: "Professional antenatal check-ups, midwife consultations, and maternal healthcare delivered to your home in Uganda. Expert care for expectant mothers - by Yitalife, a service of Yita Medical Services (YMS) Kampala.",
    image: antenatalImg,
    imageAlt: "Midwife conducting antenatal care visit for expectant mother in Uganda - Yitalife",
    icon: "woman-outline",
    intro: "Yitalife's maternal and antenatal care service ensures every expectant mother in Uganda receives the professional attention she deserves - without the stress of crowded clinics. Our qualified midwives and doctors visit you at home for antenatal check-ups, health education, and birth planning.",
    benefits: [
      "Qualified midwives and OB/GYN doctors at your home",
      "Personalised birth plan and maternal health education",
      "Routine antenatal check-ups and foetal monitoring",
      "No transport stress during pregnancy",
      "Continuity of care from conception to postpartum",
      "Safe and private consultations",
    ],
    covered: ["Antenatal check-ups", "Foetal heart monitoring", "Blood pressure & urine checks", "Nutritional counselling", "Family planning", "Postnatal home visits"],
    canonical: "https://www.yitalife.com/services/antenatal-maternal-care",
  },
  "corporate-health-assessment": {
    title: "Corporate Health Assessment Uganda - Staff Medical Checks",
    heading: "Corporate Staff Health Assessment",
    description: "On-site corporate health assessments and staff medical check-ups for companies in Uganda. Yitalife brings qualified doctors to your workplace for comprehensive health screening. A service of Yita Medical Services (YMS).",
    image: corporateImg,
    imageAlt: "Corporate health assessment and staff medical check at a company in Uganda - Yitalife",
    icon: "business-outline",
    intro: "Healthy employees are productive employees. Yitalife's Corporate Health Assessment service sends qualified medical professionals directly to your workplace in Kampala and across Uganda - saving your team hours of clinic visits while providing comprehensive health data for your organisation.",
    benefits: [
      "Minimal disruption - doctors come to your office",
      "Comprehensive health data for HR and management",
      "Confidential individual results for each employee",
      "Includes chronic disease screening and mental wellness",
      "Fully compliant with occupational health standards",
      "Available for teams of any size",
    ],
    covered: ["Blood pressure & glucose screening", "BMI & vision checks", "Mental health assessments", "HIV & STI counselling", "Lifestyle disease screening", "Full medical report"],
    canonical: "https://www.yitalife.com/services/corporate-health-assessment",
  },
  "telemedicine": {
    title: "Telemedicine Uganda - Online Doctor Consultation",
    heading: "Telemedicine - Virtual Doctor Consultations",
    description: "Book an online doctor consultation from anywhere in Uganda. Yitalife's telemedicine service connects you with qualified doctors via video or phone - fast, affordable, and professional. A service of Yita Medical Services (YMS).",
    image: telemedImg,
    imageAlt: "Telemedicine online doctor consultation in Uganda - Yitalife virtual healthcare",
    icon: "videocam-outline",
    intro: "Yitalife's telemedicine service makes quality medical consultations accessible to everyone in Uganda - no matter where they are. Connect with a qualified doctor via phone or video call for a diagnosis, prescription, or medical advice within minutes.",
    benefits: [
      "Consult a doctor without leaving home",
      "Available across all of Uganda, including rural areas",
      "Quick turnaround - consultations within 1-2 hours",
      "Prescriptions sent digitally or to your nearest pharmacy",
      "Ideal for follow-up appointments and chronic conditions",
      "Private, secure, and confidential",
    ],
    covered: ["General medical consultations", "Follow-up reviews", "Mental health support", "Prescription renewals", "Specialist referrals", "Paediatric consultations"],
    canonical: "https://www.yitalife.com/services/telemedicine",
  },
  "chronic-disease-care": {
    title: "Chronic Disease Care & Management at Home - Uganda",
    heading: "Chronic Disease Follow-up & Care",
    description: "Expert management of diabetes, hypertension, asthma, HIV, and other chronic conditions at your home in Uganda. Yitalife's doctors monitor and support your long-term health. A service of Yita Medical Services (YMS).",
    image: chronicImg,
    imageAlt: "Doctor monitoring chronic disease patient at home in Uganda - Yitalife",
    icon: "refresh-circle-outline",
    intro: "Managing a chronic condition is a long-term commitment. Yitalife's Chronic Disease Care service provides ongoing medical supervision and support from the comfort of your home - reducing the burden of frequent hospital visits while keeping your health on track.",
    benefits: [
      "Regular home visits by the same doctor for continuity",
      "Medication management and prescription renewals",
      "Monitoring of vital signs and disease progression",
      "Personalised care plans tailored to your condition",
      "Emergency escalation support when needed",
      "Family education and caregiver guidance",
    ],
    covered: ["Diabetes monitoring", "Hypertension management", "Asthma & respiratory care", "HIV/AIDS follow-up", "Cancer supportive care", "Post-stroke rehabilitation support"],
    canonical: "https://www.yitalife.com/services/chronic-disease-care",
  },
  "school-medical-examination": {
    title: "School Medical Examinations Uganda - Student Health Checks",
    heading: "School Student Medical Examinations",
    description: "On-site medical examinations and health screenings for schools in Uganda. Yitalife sends qualified medical teams to your school for student health checks and wellness assessments. A service of Yita Medical Services (YMS).",
    image: schoolImg,
    imageAlt: "Medical team conducting school student health examinations in Uganda - Yitalife",
    icon: "school-outline",
    intro: "Yitalife partners with schools across Uganda to provide on-site student medical examinations, ensuring every child is healthy and fit for learning. Our qualified medical teams visit your school campus, minimising disruption while delivering comprehensive health assessments.",
    benefits: [
      "Qualified doctors and nurses come to your school",
      "No need to transport students to clinics",
      "Detailed health report for school administration",
      "Parental consent and notification process included",
      "Early detection of vision, hearing, and health issues",
      "Seasonal vaccination and deworming integration",
    ],
    covered: ["Physical examinations", "Vision and hearing tests", "Dental check-ups", "BMI and growth monitoring", "Mental health screening", "Vaccination status review"],
    canonical: "https://www.yitalife.com/services/school-medical-examination",
  },
  "remote-patient-monitoring": {
    title: "Remote Patient Monitoring Uganda",
    heading: "Remote Patient Monitoring",
    description: "Technology-enabled remote monitoring of patients in Uganda. Yitalife tracks your vital signs and health data from home, ensuring your recovery is safe and well-supported. A service of Yita Medical Services (YMS).",
    image: remoteImg,
    imageAlt: "Remote patient monitoring with health technology in Uganda - Yitalife",
    icon: "pulse-outline",
    intro: "Yitalife uses technology to monitor patients remotely, tracking vital signs, medication adherence, and recovery progress without requiring daily physical visits. Ideal for post-surgical patients, the elderly, and those managing chronic conditions.",
    benefits: [
      "Continuous health monitoring without hospital admission",
      "Alerts sent to medical team if readings are abnormal",
      "Reduces unnecessary emergency visits",
      "Patient and family can track health data in real time",
      "Paired with regular virtual or physical check-ins",
      "Cost-effective long-term care solution",
    ],
    covered: ["Blood pressure monitoring", "Blood glucose tracking", "Oxygen saturation", "ECG monitoring", "Weight and BMI tracking", "Medication reminders"],
    canonical: "https://www.yitalife.com/services/remote-patient-monitoring",
  },
  "mental-health": {
    title: "Mental Health Services Uganda - Counselling & Psychiatry",
    heading: "Mental Health Services",
    description: "Professional mental health support, counselling, and psychiatric care at home or online in Uganda. Yitalife connects you with qualified psychologists and psychiatrists. A service of Yita Medical Services (YMS).",
    image: teamImg,
    imageAlt: "Mental health consultation and counselling in Uganda - Yitalife",
    icon: "heart-circle-outline",
    intro: "Mental health is as important as physical health. Yitalife's mental health services provide confidential, professional support from qualified psychologists and psychiatrists - delivered at home, at your workplace, or via telemedicine.",
    benefits: [
      "Confidential consultations in the privacy of your home",
      "Qualified psychiatrists and clinical psychologists",
      "No stigma - care where you feel safest",
      "Integrated with physical health services",
      "Available for individuals, couples, and groups",
      "Crisis support and emergency mental health referrals",
    ],
    covered: ["Depression & anxiety counselling", "Trauma and PTSD support", "Addiction counselling", "Stress management", "Psychiatric assessment & medication", "Corporate wellness programmes"],
    canonical: "https://www.yitalife.com/services/mental-health",
  },
  "community-outreach": {
    title: "Community Health Outreach Uganda - Free Medical Camps",
    heading: "Community Health Outreach",
    description: "Yitalife conducts free community medical camps, health screenings, and outreach programmes across Uganda. Bringing quality healthcare to underserved communities. A service of Yita Medical Services (YMS).",
    image: communityImg,
    imageAlt: "Community health outreach medical camp in Kampala Uganda - Yitalife",
    icon: "people-outline",
    intro: "Yitalife believes no Ugandan should miss out on quality healthcare due to geography or cost. Our community outreach programmes bring free or subsidised medical camps, health education, and screenings to underserved communities across Uganda.",
    benefits: [
      "Free health screenings and consultations",
      "Reaches rural and peri-urban communities",
      "Health education and awareness campaigns",
      "Referral support for patients needing further care",
      "Partnerships with local leaders and organisations",
      "Vaccines, deworming, and essential medicines distributed",
    ],
    covered: ["Free general consultations", "Blood pressure & sugar screening", "HIV testing and counselling", "Maternal health education", "Child immunisations", "Health awareness talks"],
    canonical: "https://www.yitalife.com/services/community-outreach",
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = services[slug];

  if (!svc) {
    return <Navigate to="/Services" replace />;
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": svc.heading,
    "description": svc.description,
    "url": svc.canonical,
    "provider": {
      "@type": "MedicalOrganization",
      "name": "Yitalife - Yita Medical Services",
      "url": "https://www.yitalife.com/",
      "telephone": "+256777114327",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Kampala",
        "addressCountry": "UG"
      }
    }
  };

  return (
    <div>
      <PageSEO
        title={svc.title}
        description={svc.description}
        canonical={svc.canonical}
        ogImage={svc.image}
        structuredData={structuredData}
      />

      {/* Hero */}
      <div className="service-detail-hero">
        <img
          src={svc.image}
          alt={svc.imageAlt}
          className="service-detail-hero-img"
        />
        <div className="service-detail-hero-overlay" aria-hidden="true"></div>
        <div className="container service-detail-hero-content">
          <div className="service-detail-icon">
            <ion-icon name={svc.icon}></ion-icon>
          </div>
          <h1 data-aos="fade-down">{svc.heading}</h1>
          <div className="page-breadcrumb" style={{ justifyContent: "center", marginTop: "12px" }}>
            <Link to="/">Home</Link>
            <ion-icon name="chevron-forward-outline" style={{ fontSize: "13px" }}></ion-icon>
            <Link to="/Services">Services</Link>
            <ion-icon name="chevron-forward-outline" style={{ fontSize: "13px" }}></ion-icon>
            <span>{svc.heading}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="service-detail-body">
        <div className="container">
          <div className="row g-5">
            {/* Main content */}
            <div className="col-lg-8" data-aos="fade-right">
              <div className="badge-yita" style={{ marginBottom: "20px" }}>
                <ion-icon name="shield-checkmark-outline"></ion-icon>
                Yitalife - a service of Yita Medical Services (YMS)
              </div>
              <h2>{svc.heading}</h2>
              <p className="service-detail-intro">{svc.intro}</p>

              <h3 className="service-detail-sub">Why Choose Yitalife?</h3>
              <ul className="service-detail-benefits">
                {svc.benefits.map((b, i) => (
                  <li key={i}>
                    <ion-icon name="checkmark-circle-outline"></ion-icon>
                    {b}
                  </li>
                ))}
              </ul>

              <h3 className="service-detail-sub">What's Covered</h3>
              <div className="row g-2">
                {svc.covered.map((item, i) => (
                  <div className="col-md-6" key={i}>
                    <div className="service-covered-item">
                      <ion-icon name="medical-outline"></ion-icon> {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4" data-aos="fade-left" data-aos-delay="100">
              <div className="service-detail-sidebar">
                <h4>Book This Service</h4>
                <p>Get qualified medical professionals at your location, fast.</p>
                <Link to="/Appointment">
                  <button className="service-detail-book-btn">
                    <ion-icon name="calendar-outline"></ion-icon> Book Now
                  </button>
                </Link>
                <div className="service-sidebar-contact">
                  <a href="tel:+256777114327" className="service-sidebar-call">
                    <ion-icon name="call-outline"></ion-icon> +256 777 114 327
                  </a>
                  <a href="https://wa.me/256777114327" target="_blank" rel="noreferrer" className="service-sidebar-whatsapp">
                    <ion-icon name="logo-whatsapp"></ion-icon> WhatsApp Us
                  </a>
                </div>

                <div className="service-sidebar-hours">
                  <h6><ion-icon name="alarm-outline"></ion-icon> Operating Hours</h6>
                  <p>Mon - Fri: 8:30 am - 11:30 pm</p>
                  <p>Sat - Sun: 10:00 am - 11:30 pm</p>
                  <p className="service-sidebar-emergency">
                    <ion-icon name="warning-outline"></ion-icon> 24/7 Emergency Line
                  </p>
                </div>

                <div className="service-sidebar-area">
                  <h6><ion-icon name="location-outline"></ion-icon> Service Areas</h6>
                  <p>Kampala, Wakiso, Mukono, Entebbe &amp; Central Uganda.<br />Telemedicine: Nationwide.</p>
                </div>

                <div className="service-sidebar-other">
                  <h6>Other Services</h6>
                  <Link to="/Services">View all services →</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-detail-cta">
        <div className="container text-center" data-aos="fade-up">
          <h2>Ready to Get Started?</h2>
          <p>Book a {svc.heading} appointment with Yitalife today.</p>
          <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/Appointment">
              <button className="service-detail-cta-btn-primary">Book an Appointment</button>
            </Link>
            <Link to="/Contact">
              <button className="service-detail-cta-btn-outline">Contact Us</button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
